import {
  Goal,
  GroupByMode,
  GroupedSection,
  GroupSectionType,
  ID,
  LifeArea,
  noPriorityOption,
  SortingMode,
  Tag,
  Task,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getLinkedItemColor } from './get-linked-item-color';
import { getSortedTasks } from './get-sorted-tasks';
import {
  groupTasksByDayPart,
  GroupTasksByDayPartLabels,
} from './group-tasks-by-day-part';
import { groupTasksByDeadline } from './group-tasks-by-deadline';
import { groupTasksByDeadlineDeadline } from './group-tasks-by-deadline-deadline';
import { groupTasksByGoal } from './group-tasks-by-goal';
import { groupTasksByLifeArea } from './group-tasks-by-life-area';
import { groupTasksByNone } from './group-tasks-by-none';
import { groupTasksByPriority } from './group-tasks-by-priority';
import { groupTasksByTag } from './group-tasks-by-tag';
import { groupTasksByType } from './group-tasks-by-type';

const PRIORITY_LABELS: Record<string, string> = {
  [TaskPriorityOptions.High]: 'High',
  [TaskPriorityOptions.Medium]: 'Medium',
  [TaskPriorityOptions.Low]: 'Low',
  [noPriorityOption]: 'None',
};

export type BuildGroupedSortedSectionsOptions = {
  tasks: Task[];
  groupByMode: GroupByMode;
  sortingMode?: SortingMode | null;
  customOrder?: { [GroupByMode.None]: ID[] } | Record<ID, ID[]>;
  lifeAreas: LifeArea[];
  goals: Goal[];
  tags: Tag[];
  taskTagsMap: Map<ID, ID[]>;
  lifeAreaSorting?: ID[];
  goalSorting?: ID[];
  tagSorting?: ID[];
  today: Date;
  weekStartsOn: WeekDays;
  dateFilterEndDate?: Timestamp | null;
  dayPartLabels?: GroupTasksByDayPartLabels;
};

const buildSections = ({
  tasks,
  groupByMode,
  sortingMode,
  customOrder,
  lifeAreas,
  goals,
  tags,
  taskTagsMap,
  lifeAreaSorting,
  goalSorting,
  tagSorting,
  today,
  weekStartsOn,
  dateFilterEndDate,
  dayPartLabels,
}: BuildGroupedSortedSectionsOptions): GroupedSection[] => {
  if (groupByMode === GroupByMode.None) {
    return groupTasksByNone(tasks);
  }

  if (groupByMode === GroupByMode.LifeArea) {
    const groups = groupTasksByLifeArea({
      tasks,
      lifeAreas,
      lifeAreaSorting,
      goals,
    });
    return groups.map((g) => ({
      sectionId: g.lifeAreaId,
      sectionType: GroupSectionType.LifeArea,
      name: g.lifeArea?.name ?? 'Other',
      iconName: g.lifeArea?.iconName ?? undefined,
      imageUrl: g.lifeArea?.visionPreview?.image?.url ?? null,
      color: g.lifeArea?.color ?? null,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Goal) {
    const groups = groupTasksByGoal({ tasks, goals, goalSorting });
    return groups.map((g) => ({
      sectionId: g.goalId,
      sectionType: GroupSectionType.Goal,
      name: g.goal?.name ?? 'Other',
      iconName: g.goal?.iconName ?? undefined,
      imageUrl: g.goal?.image?.url ?? null,
      color: g.goal
        ? getLinkedItemColor(g.goal.id, { goals, lifeAreas })
        : null,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Priority) {
    const groups = groupTasksByPriority({ tasks });
    return groups.map((g) => ({
      sectionId: g.priority,
      sectionType: GroupSectionType.Priority,
      name: PRIORITY_LABELS[g.priority] ?? g.priority,
      tasks: g.tasks,
    }));
  }

  if (groupByMode === GroupByMode.Type) {
    return groupTasksByType(tasks);
  }

  if (groupByMode === GroupByMode.Tag) {
    return groupTasksByTag({ tasks, tags, taskTagsMap, tagSorting });
  }

  if (groupByMode === GroupByMode.Deadline) {
    return groupTasksByDeadline({
      tasks,
      today,
      weekStartsOn,
      dateFilterEndDate,
      sortingMode,
      customOrder: customOrder as Record<ID, ID[]> | undefined,
    });
  }

  if (groupByMode === GroupByMode.DeadlineDeadline) {
    return groupTasksByDeadlineDeadline(tasks, today);
  }

  if (groupByMode === GroupByMode.DayPart) {
    return groupTasksByDayPart({
      tasks,
      today,
      weekStartsOn,
      customOrder: customOrder as Record<ID, ID[]> | undefined,
      sortingMode,
      labels: dayPartLabels,
    });
  }

  return groupTasksByNone(tasks);
};

export const buildGroupedSortedSections = (
  options: BuildGroupedSortedSectionsOptions,
): GroupedSection[] => {
  const { sortingMode, customOrder, groupByMode, today, weekStartsOn } =
    options;

  const sections = buildSections(options);

  // Pre-sorted by their groupers; overdue uses invertedTimeslots the generic sort would overwrite.
  if (
    groupByMode === GroupByMode.DayPart ||
    groupByMode === GroupByMode.Deadline
  ) {
    return sections;
  }

  // Sort tasks within each section
  return sections.map((section) => {
    const orderForSection =
      sortingMode === SortingMode.Custom
        ? groupByMode === GroupByMode.None
          ? customOrder?.[GroupByMode.None]
          : (customOrder as Record<ID, ID[]> | undefined)?.[section.sectionId]
        : undefined;

    const sortedTasks = getSortedTasks(section.tasks, {
      today,
      weekStartsOn,
      order: orderForSection ?? [],
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    return { ...section, tasks: sortedTasks };
  });
};
